// components/calls/AdvancedFilters.jsx
import React, { useState, useEffect, useMemo } from "react";
import { X, Filter, Search } from "lucide-react";
import api from "../../services/api";

// ─── Searchable agent list (inline, no absolute positioning) ───────────────────
const AgentSearchDropdown = ({ options, value, onChange, placeholder }) => {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return q
      ? options.filter(
          (o) =>
            o.label.toLowerCase().includes(q) ||
            o.value.toLowerCase().includes(q),
        )
      : options;
  }, [options, search]);

  return (
    <div className="flex flex-col gap-2">
      {/* Search input */}
      <div className="relative">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          className="w-full pl-8 pr-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500"
        />
      </div>

      {/* Inline list — fills available space, scrolls within the tab content area */}
      <ul
        className="bg-gray-800/60 border border-gray-700 rounded-lg overflow-y-auto"
        style={{ maxHeight: "340px" }}
      >
        {/* "All agents" option */}
        <li>
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setSearch("");
            }}
            className={`w-full text-left px-3 py-2.5 text-sm transition-colors hover:bg-gray-700/60 ${
              !value
                ? "text-purple-400 bg-gray-700/40 font-medium"
                : "text-gray-400"
            }`}
          >
            {placeholder || "All agents"}
          </button>
        </li>

        {filtered.map((o) => (
          <li key={o.value}>
            <button
              type="button"
              onClick={() => {
                onChange(o.value);
                setSearch("");
              }}
              className={`w-full text-left px-3 py-2.5 text-sm transition-colors hover:bg-gray-700/60 ${
                value === o.value
                  ? "text-purple-400 bg-gray-700/40"
                  : "text-white"
              }`}
            >
              <div className="font-medium">{o.label}</div>
              {o.sub && (
                <div className="text-gray-500 text-xs mt-0.5">{o.sub}</div>
              )}
            </button>
          </li>
        ))}

        {filtered.length === 0 && (
          <li className="px-3 py-3 text-gray-500 text-sm text-center">
            No results
          </li>
        )}
      </ul>
    </div>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────────
const AdvancedFilters = ({
  filters,
  onFilterChange,
  onClose,
  filterOptions,
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [activeSection, setActiveSection] = useState("basic");

  // Agent lists fetched separately for accuracy
  const [webexUsers, setWebexUsers] = useState([]);
  const [callSwitchExtensions, setCallSwitchExtensions] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  // Fetch agent lists on mount
  useEffect(() => {
    const load = async () => {
      setAgentsLoading(true);
      try {
        const [webexRes, csRes] = await Promise.all([
          api.getWebexUsers(false).catch(() => null),
          api.getCallSwitchExtensions({}).catch(() => null),
        ]);

        if (webexRes?.users) {
          setWebexUsers(webexRes.users);
        }
        if (csRes?.extensions) {
          setCallSwitchExtensions(csRes.extensions);
        }
      } catch (e) {
        console.error("Failed to load agent lists", e);
      } finally {
        setAgentsLoading(false);
      }
    };
    load();
  }, []);

  const handleChange = (key, value) => {
    setLocalFilters((prev) => {
      const next = { ...prev, [key]: value };

      // When platform changes, clear agent-specific filters so they don't bleed across
      if (key === "platform") {
        next.extensionNumber = null;
        next.agentEmail = null;
      }

      return next;
    });
  };

  const handleApply = () => {
    onFilterChange(localFilters);
    onClose();
  };

  const handleReset = () => {
    const reset = {
      status: "all",
      sentiment: null,
      direction: null,
      qualificationLevel: null,
      isQualified: null,
      flagged: null,
      appointmentBooked: null,
      hasEmail: null,
      hasFullName: null,
      platform: null,
      extensionNumber: null,
      agentEmail: null,
      minDuration: null,
      maxDuration: null,
      tags: null,
      startDate: null,
      endDate: null,
      search: "",
      sortBy: "callStartTime",
      sortOrder: "DESC",
    };
    setLocalFilters(reset);
    onFilterChange(reset);
  };

  const activeFilterCount = Object.entries(localFilters).filter(
    ([k, v]) =>
      !["sortBy", "sortOrder", "page", "limit"].includes(k) &&
      v !== null &&
      v !== "" &&
      v !== "all",
  ).length;

  // ── Derive agent options based on selected platform ──────────────────────────
  const platform = localFilters.platform;

  const webexAgentOptions = useMemo(
    () =>
      webexUsers
        .filter((u) => u.isActive !== false)
        .map((u) => ({
          value: u.webexUserEmail,
          label: u.webexDisplayName || u.userDisplayName || u.webexUserEmail,
          sub: u.webexUserEmail,
        })),
    [webexUsers],
  );

  const callSwitchAgentOptions = useMemo(
    () =>
      callSwitchExtensions
        .filter((e) => e.isActive !== false)
        .map((e) => ({
          value: e.extensionNumber,
          label:
            e.extensionName ||
            e.callSwitchName ||
            `Extension ${e.extensionNumber}`,
          sub: `Ext. ${e.extensionNumber}${e.callSwitchEmail ? ` · ${e.callSwitchEmail}` : ""}`,
        })),
    [callSwitchExtensions],
  );

  // Fallback: if no platform selected, offer both
  const genericExtensionOptions = useMemo(
    () =>
      (filterOptions?.extensions || []).map((ext) => ({
        value: ext,
        label: `Extension ${ext}`,
        sub: ext,
      })),
    [filterOptions],
  );

  // ── Agent filter section renders differently per platform ────────────────────
  const renderAgentFilter = () => {
    if (platform === "webex") {
      return (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Webex Agent
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Filters by{" "}
            <code className="text-purple-400">metadata.webexOwnerEmail</code>
          </p>
          {agentsLoading ? (
            <div className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-500 text-sm">
              Loading agents…
            </div>
          ) : (
            <AgentSearchDropdown
              options={webexAgentOptions}
              value={localFilters.agentEmail}
              onChange={(v) => handleChange("agentEmail", v)}
              placeholder="All Webex agents"
            />
          )}
          {webexAgentOptions.length === 0 && !agentsLoading && (
            <p className="mt-1 text-xs text-amber-400">
              No Webex users found. Ensure Webex users are configured.
            </p>
          )}
        </div>
      );
    }

    if (platform === "callswitch") {
      return (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            CallSwitch Extension / Agent
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Filters by <code className="text-purple-400">extensionNumber</code>
          </p>
          {agentsLoading ? (
            <div className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-500 text-sm">
              Loading extensions…
            </div>
          ) : (
            <AgentSearchDropdown
              options={callSwitchAgentOptions}
              value={localFilters.extensionNumber}
              onChange={(v) => handleChange("extensionNumber", v)}
              placeholder="All extensions"
            />
          )}
          {callSwitchAgentOptions.length === 0 && !agentsLoading && (
            <p className="mt-1 text-xs text-amber-400">
              No CallSwitch extensions found. Run a discovery first.
            </p>
          )}
        </div>
      );
    }

    // No platform selected — offer generic extension picker
    return (
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Agent / Extension
        </label>
        <p className="text-xs text-gray-500 mb-2">
          Select a platform above to filter by Webex agent or CallSwitch
          extension specifically.
        </p>
        <AgentSearchDropdown
          options={genericExtensionOptions}
          value={localFilters.extensionNumber}
          onChange={(v) => handleChange("extensionNumber", v)}
          placeholder="All extensions"
        />
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative bg-[#0C0E12] border border-gray-700 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div className="sticky top-0 bg-[#0C0E12] border-b border-gray-700 p-6 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Filter size={24} />
                Advanced Filters
              </h2>
              <p className="text-gray-400 mt-1">
                {activeFilterCount} filter{activeFilterCount !== 1 ? "s" : ""}{" "}
                active
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white p-2 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Section tabs */}
          <div className="flex gap-2 mt-4">
            {["basic", "agent", "qualification", "advanced", "sorting"].map(
              (section) => (
                <button
                  key={section}
                  onClick={() => setActiveSection(section)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === section
                      ? "bg-purple-600 text-white"
                      : "bg-gray-800 text-gray-400 hover:text-white"
                  }`}
                >
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                  {/* Dot indicator if this section has active filters */}
                  {section === "agent" &&
                    (localFilters.extensionNumber ||
                      localFilters.agentEmail) && (
                      <span className="ml-1.5 inline-block w-2 h-2 bg-purple-400 rounded-full" />
                    )}
                </button>
              ),
            )}
          </div>
        </div>

        {/* ── Content ────────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* ── Basic ── */}
          {activeSection === "basic" && (
            <div className="space-y-6">
              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={localFilters.status || "all"}
                  onChange={(e) => handleChange("status", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                >
                  <option value="all">All Status</option>
                  {filterOptions?.statuses?.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Platform — important: determines agent filter mode */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Platform
                </label>
                <select
                  value={localFilters.platform || ""}
                  onChange={(e) =>
                    handleChange("platform", e.target.value || null)
                  }
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                >
                  <option value="">All Platforms</option>
                  {(filterOptions?.platforms || ["webex", "callswitch"]).map(
                    (p) => (
                      <option key={p} value={p}>
                        {p === "webex"
                          ? "Webex"
                          : p === "callswitch"
                            ? "CallSwitch"
                            : p.charAt(0).toUpperCase() + p.slice(1)}
                      </option>
                    ),
                  )}
                </select>
                {localFilters.platform && (
                  <p className="mt-1 text-xs text-purple-400">
                    Agent filter in the <strong>Agent</strong> tab is now scoped
                    to{" "}
                    {localFilters.platform === "webex"
                      ? "Webex users"
                      : "CallSwitch extensions"}
                    .
                  </p>
                )}
              </div>

              {/* Sentiment */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Sentiment
                </label>
                <select
                  value={localFilters.sentiment || ""}
                  onChange={(e) =>
                    handleChange("sentiment", e.target.value || null)
                  }
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                >
                  <option value="">All Sentiments</option>
                  <option value="positive">Positive</option>
                  <option value="neutral">Neutral</option>
                  <option value="negative">Negative</option>
                </select>
              </div>

              {/* Direction */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Call Direction
                </label>
                <select
                  value={localFilters.direction || ""}
                  onChange={(e) =>
                    handleChange("direction", e.target.value || null)
                  }
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                >
                  <option value="">All Directions</option>
                  <option value="inbound">Inbound</option>
                  <option value="outbound">Outbound</option>
                </select>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={localFilters.startDate || ""}
                    onChange={(e) =>
                      handleChange("startDate", e.target.value || null)
                    }
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={localFilters.endDate || ""}
                    onChange={(e) =>
                      handleChange("endDate", e.target.value || null)
                    }
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
              </div>

              {/* Duration Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Min Duration (seconds)
                  </label>
                  <input
                    type="number"
                    value={localFilters.minDuration || ""}
                    onChange={(e) =>
                      handleChange(
                        "minDuration",
                        e.target.value ? parseInt(e.target.value) : null,
                      )
                    }
                    placeholder="e.g. 30"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Max Duration (seconds)
                  </label>
                  <input
                    type="number"
                    value={localFilters.maxDuration || ""}
                    onChange={(e) =>
                      handleChange(
                        "maxDuration",
                        e.target.value ? parseInt(e.target.value) : null,
                      )
                    }
                    placeholder="e.g. 600"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Agent ── */}
          {activeSection === "agent" && (
            <div className="space-y-6">
              {/* Platform reminder */}
              <div className="p-3 bg-gray-800/60 border border-gray-700 rounded-lg">
                {platform === "webex" ? (
                  <p className="text-sm text-gray-300">
                    <span className="text-purple-400 font-medium">
                      Webex mode
                    </span>{" "}
                    — selecting an agent filters by{" "}
                    <code className="text-xs bg-gray-700 px-1 py-0.5 rounded">
                      metadata.webexOwnerEmail
                    </code>
                    .
                  </p>
                ) : platform === "callswitch" ? (
                  <p className="text-sm text-gray-300">
                    <span className="text-purple-400 font-medium">
                      CallSwitch mode
                    </span>{" "}
                    — selecting an extension filters by{" "}
                    <code className="text-xs bg-gray-700 px-1 py-0.5 rounded">
                      extensionNumber
                    </code>
                    .
                  </p>
                ) : (
                  <p className="text-sm text-gray-300">
                    Select a <strong>Platform</strong> in the Basic tab to
                    unlock precise agent filtering. Without a platform, you can
                    still filter by extension number.
                  </p>
                )}
              </div>

              {renderAgentFilter()}

              {/* Show current active agent filter summary */}
              {(localFilters.agentEmail || localFilters.extensionNumber) && (
                <div className="p-3 bg-purple-900/20 border border-purple-700/40 rounded-lg text-sm">
                  <p className="text-purple-300 font-medium mb-1">
                    Active agent filter
                  </p>
                  {localFilters.agentEmail && (
                    <div className="flex items-center justify-between text-gray-300">
                      <span>
                        <span className="text-gray-500">Webex email:</span>{" "}
                        {localFilters.agentEmail}
                      </span>
                      <button
                        onClick={() => handleChange("agentEmail", null)}
                        className="text-gray-500 hover:text-white ml-2"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  {localFilters.extensionNumber && (
                    <div className="flex items-center justify-between text-gray-300">
                      <span>
                        <span className="text-gray-500">Extension:</span>{" "}
                        {localFilters.extensionNumber}
                      </span>
                      <button
                        onClick={() => handleChange("extensionNumber", null)}
                        className="text-gray-500 hover:text-white ml-2"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Qualification ── */}
          {activeSection === "qualification" && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Qualification Level
                </label>
                <select
                  value={localFilters.qualificationLevel || ""}
                  onChange={(e) =>
                    handleChange("qualificationLevel", e.target.value || null)
                  }
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                >
                  <option value="">All Levels</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                  <option value="unqualified">Unqualified</option>
                </select>
              </div>

              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localFilters.isQualified === "true"}
                    onChange={(e) =>
                      handleChange(
                        "isQualified",
                        e.target.checked ? "true" : null,
                      )
                    }
                    className="w-5 h-5 rounded border-gray-700 bg-gray-800 text-purple-600 focus:ring-purple-600"
                  />
                  <span className="text-gray-300">Qualified leads only</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localFilters.appointmentBooked === "true"}
                    onChange={(e) =>
                      handleChange(
                        "appointmentBooked",
                        e.target.checked ? "true" : null,
                      )
                    }
                    className="w-5 h-5 rounded border-gray-700 bg-gray-800 text-purple-600 focus:ring-purple-600"
                  />
                  <span className="text-gray-300">Appointment booked</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localFilters.hasEmail === "true"}
                    onChange={(e) =>
                      handleChange("hasEmail", e.target.checked ? "true" : null)
                    }
                    className="w-5 h-5 rounded border-gray-700 bg-gray-800 text-purple-600 focus:ring-purple-600"
                  />
                  <span className="text-gray-300">Has email address</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localFilters.hasFullName === "true"}
                    onChange={(e) =>
                      handleChange(
                        "hasFullName",
                        e.target.checked ? "true" : null,
                      )
                    }
                    className="w-5 h-5 rounded border-gray-700 bg-gray-800 text-purple-600 focus:ring-purple-600"
                  />
                  <span className="text-gray-300">Has full name</span>
                </label>
              </div>
            </div>
          )}

          {/* ── Advanced ── */}
          {activeSection === "advanced" && (
            <div className="space-y-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={localFilters.flagged === "true"}
                  onChange={(e) =>
                    handleChange("flagged", e.target.checked ? "true" : null)
                  }
                  className="w-5 h-5 rounded border-gray-700 bg-gray-800 text-purple-600 focus:ring-purple-600"
                />
                <span className="text-gray-300">Flagged for review only</span>
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={localFilters.tags || ""}
                  onChange={(e) => handleChange("tags", e.target.value || null)}
                  placeholder="e.g. urgent, follow-up, complaint"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                />
                {filterOptions?.tags && filterOptions.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="text-xs text-gray-400">
                      Available tags:
                    </span>
                    {filterOptions.tags.slice(0, 20).map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => {
                          const current =
                            localFilters.tags
                              ?.split(",")
                              .map((t) => t.trim())
                              .filter(Boolean) || [];
                          if (!current.includes(tag)) {
                            handleChange("tags", [...current, tag].join(", "));
                          }
                        }}
                        className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs text-gray-300 transition-colors"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Sorting ── */}
          {activeSection === "sorting" && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Sort By
                </label>
                <select
                  value={localFilters.sortBy || "callStartTime"}
                  onChange={(e) => handleChange("sortBy", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                >
                  <option value="callStartTime">Date & Time</option>
                  <option value="duration">Duration</option>
                  <option value="sentiment">Sentiment</option>
                  <option value="qualificationScore">
                    Qualification Score
                  </option>
                  <option value="agentName">Agent Name</option>
                  <option value="status">Status</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Sort Order
                </label>
                <select
                  value={localFilters.sortOrder || "DESC"}
                  onChange={(e) => handleChange("sortOrder", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                >
                  <option value="DESC">Descending (High to Low)</option>
                  <option value="ASC">Ascending (Low to High)</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────────── */}
        <div className="sticky bottom-0 bg-[#0C0E12] border-t border-gray-700 p-6 flex gap-3">
          {/* Reset — dark muted variant */}
          <button
            onClick={handleReset}
            className="flex-1 px-4 py-2.5 text-gray-300 rounded-lg font-medium transition-all duration-300 hover:scale-[1.02] hover:shadow-xl"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, #2D2D3A 0%, #1A1A24 100%)",
              border: "1px solid",
              borderImageSource:
                "radial-gradient(50% 50% at 50% 50%, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 100%)",
              borderImageSlice: "1",
              boxShadow: `
                0px 8px 16px 0px rgba(0,0,0,0.3),
                0px 8px 24px -16px rgba(255,255,255,0.1) inset,
                0px -12px 20px 0px rgba(255,255,255,0.05) inset
              `,
            }}
          >
            Reset All
          </button>

          {/* Apply — full purple glow variant */}
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-2.5 text-white rounded-lg font-medium transition-all duration-300 hover:scale-[1.02] hover:shadow-xl"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
              border: "1px solid",
              borderImageSource:
                "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
              borderImageSlice: "1",
              boxShadow: `
                0px 36px 32px -16px #7F56D936,
                0px 8px 16px 0px #7F56D91F,
                0px 2px 4px -8px #7F56D914,
                0px 8px 24px -16px #FFFFFF3D inset,
                0px -24px 32px 0px #FFFFFF38 inset,
                0px 0px 12px 0px #FFFFFF inset
              `,
            }}
          >
            Apply Filters ({activeFilterCount})
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedFilters;
